import { DateTime } from 'luxon';
import * as z from 'zod';

import { AllBrandedIds } from '../BrandedIds';

/**
 * JS (and xlsx) parse 2-digit years as 19xx — "1/1/50" → 1950, and an xlsx
 * cell `Date(1954-05-01)` came from "5/1/54". For forward-looking date fields
 * (maturity, paymentDate, etc.) we want 20xx instead. Shifts any pre-1990
 * date by 100 years; otherwise returns it unchanged.
 */
export const maybeImplyTwoThousands = (date: Date, implyTwoThousands: boolean): Date => {
  if (implyTwoThousands && date < new Date('1/1/1990')) {
    return DateTime.fromJSDate(date).plus({ years: 100 }).toJSDate();
  }
  return date;
};

/**
 * Apply the implyTwoThousands rule to a string or already-parsed Date. Used
 * by the Zod date transforms and by the file-import preview UI so both code
 * paths land on the same year regardless of whether the value arrived as a
 * raw string ("5/1/54") or as a Date object (e.g. xlsx-parsed cells).
 */
export const transformDateOrStringToDate = (value: string | Date, implyTwoThousands = true): Date =>
  typeof value === 'string'
    ? convertStringToDate(value, implyTwoThousands)
    : maybeImplyTwoThousands(value, implyTwoThousands);

/**
 * Imply 21st century for 2-digit years if implyTwoThousands is true
 */
export const convertStringToDate = (value: string, implyTwoThousands = true): Date => {
  // A 4-digit year in any format (5/1/1954, 1954-05-01) is the author being
  // explicit and is preserved; only 2-digit years imply a century.
  const hasFourDigitYear = /\d{4}/.test(value);
  if (hasFourDigitYear) {
    return new Date(value);
  }
  return maybeImplyTwoThousands(new Date(value), implyTwoThousands);
};

/**
 * 2-digit years are interpreted as 20xx
 * Should be used for things like maturity date where 1/1/50 should be 1/1/2050
 */
export const zodDateOrString = z
  .date()
  .or(z.string())
  .transform((value) => transformDateOrStringToDate(value, true))
  .meta({
    id: 'dateString',
    type: 'string',
    effectType: 'input',
    description:
      'A date string is any string parsable with new Date(<string>) in Javascript. Examples include formatted dates such as “10/1/2022” or an ISO 8601 timestamp.',
  });

export const zodDateOrStringImplyNineteenHundreds = z
  .date()
  .or(z.string())
  .transform((value) => transformDateOrStringToDate(value, false))
  .meta({
    type: 'string',
    effectType: 'input',
    description:
      'A date string is any string parsable with new Date(<string>) in Javascript. Examples include formatted dates such as “10/1/2022” or an ISO 8601 timestamp.',
  });

export function zodBrandedUuid<T extends AllBrandedIds>() {
  return z
    .uuid()
    .transform((id) => id as T)
    .meta({ type: 'string', format: 'uuid', effectType: 'input' });
}

export function zodBrandedString<T extends AllBrandedIds>() {
  return z
    .string()
    .min(1)
    .transform((id) => id as T);
}

export const zodOptionalString = z
  .string()
  .nullable()
  .optional()
  .transform((item) => (item == null ? undefined : item));

export const trimAndStripString = (val: any) => {
  // Trim strings
  if (typeof val === 'string') {
    val = val.trim();
  }
  // Turn "empty" values into undefined
  if (val === null || val === '') {
    return undefined;
  }
  return val;
};

export const DueMonthDay = z.string().refine(
  (val) => {
    const result = DateTime.fromFormat(val, 'MM/d');
    const result2 = DateTime.fromFormat(val, 'M/d');
    return result.isValid || result2.isValid;
  },
  {
    error: 'Not valid month day',
  },
);

export type DueMonthDay = z.infer<typeof DueMonthDay>;

export const transformNullOrUndefinedToUndefined = (value: any | null | undefined) =>
  value === null ? undefined : value;

/**
 * This is a utility function that will attempt to the validate the input
 * type BEFORE calling `.parse` on it. It should help to catch simple syntax
 * errors so we can avoid runtime errors when possible. If you didn't want
 * to use this function to handle this kind of stuff you could also do:
 *
 * const CustomEvent = z.object({ type: z.literal('custom'), name: z.string() })
 * type TCustomEvent = z.infer<typeof CustomEvent>;
 *
 * DUMB PARSING STRATEGY:
 * CustomEvent.parse({ type: 'wow', name: 123 }); // This won't throw TS errors, BAD!
 *
 * SMARTER PARSING STRATEGY:
 * const myEvent: TCustomEvent = { type: 'wow', name: 123 }; // This will throw a TS error, GOOD!
 * CustomEvent.parse(myEvent);
 *
 * WITH `typedParse`
 * typedParse(CustomEvent, { type: 'wow', name: 123 }); // This will throw a TS error, GOOD!
 *
 */
export const typedParse = <TInput, TOutput>(schema: z.ZodType<TOutput, TInput>, input: TInput) => schema.parse(input);

/**
 * Like typedParse, but uses `.safeParse` under the hood
 */
export const typedSafeParse = <TInput, TOutput>(schema: z.ZodType<TOutput, TInput>, input: TInput) =>
  schema.safeParse(input);
